/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import './index.less';

import { Tag } from '@bfe/moly';
import { EditIcon, PcIcon, PhoneIcon } from '@bfe/moly-icons';
import { Space, Typography } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SecuritySettingModalNames } from '@/components/SecuritySettingModalManager';
import { useMemberInfo } from '@/hooks/useMemberInfo';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useUserSecurityProfile } from '@/hooks/useUserSecurityProfile';
import { useI18nContext } from '@/i18n/I18n';
import { useSecuritySettingModal } from '@/store/useSecuritySettingModal/useSecuritySettingModal';
import { useSecuritySettingModalData } from '@/store/useSecuritySettingModal/useSecuritySettingModalData';
import { useStore } from '@/store/useStore';
import { TG_PLATFORM } from '@/utils/constants/common';
import { sensors } from '@/utils/skynet';

import { LEVELS } from '../kycAuth/KycAuth';
import SecurityItem from './comps/securityItem';
import {
  AvatarIcon,
  RightIcon,
  SecurityHighLevel,
  SecurityLowLevel,
  SecurityMiddleLevel,
  TimeLock,
} from './icons';

const VipLevelIcon = React.lazy(() => import('./VipLevelIcon'));

const NickNameModal = React.lazy(
  () => import('@/components/ModifyNickNameModal'),
);

const { Paragraph } = Typography;

const securityIconClass = [
  '',
  'low-icon-container',
  'middle-icon-container',
  'high-icon-container',
];

const securityLevelIcon = [
  '',
  SecurityLowLevel,
  SecurityMiddleLevel,
  SecurityHighLevel,
];

const securityClass = ['', 'low-arrow', 'middle-arrow', 'high-arrow'];

const NickNameSecurityLevel = () => {
  const [$t, lang] = useI18nContext();
  const { copyTraderInfo, copyTradingPro } = useMemberInfo();
  const { avatar } = useSecuritySettingModalData();
  const [changeName, setChangeName] = useState<string>('');
  const levelText = [
    '',
    $t('Low Level'),
    $t('Medium Level'),
    $t('High Level New'),
  ];
  const securityNoticeText = [
    '',
    $t('Low Level Tips'),
    $t('Medium Level Tips'),
    $t('High Level Tips'),
  ];
  const { kyc } = useStore();

  const navigate = useNavigate();

  const { profile, isSubmember, updateUserProfile, isCopyTrader } =
    useUserProfile();

  const { openSecuritySettingModal } = useSecuritySettingModal();

  const level = useMemo(() => {
    const isEnterprise =
      profile?.result?.member_tag &&
      profile?.result?.member_tag.KYC &&
      profile?.result?.member_tag.KYC === '1';

    if (isEnterprise) return LEVELS.level3;

    const levels: { [key: string]: number } = {
      LEVEL_DEFAULT: LEVELS.levelDefault,
      LEVEL_1: LEVELS.level1,
      LEVEL_2: LEVELS.level2,
    };

    return levels[kyc?.applicant?.level] || LEVELS.levelDefault;
  }, [profile, kyc]);

  const [visible, setVisible] = useState<boolean>(false);
  const { userSecurityInfo } = useUserSecurityProfile();
  const userSecurityLevel = useMemo(() => {
    return userSecurityInfo?.userSecurityLevel;
  }, [userSecurityInfo]);

  const latestLoginRecord = useMemo(() => {
    return userSecurityInfo?.latestLoginRecord;
  }, [userSecurityInfo]);

  const security_level = userSecurityLevel?.security_level || 1,
    suggest_options = userSecurityLevel?.suggest_options || [],
    created_at = latestLoginRecord?.created_at,
    user_agent = latestLoginRecord?.user_agent;

  const userProfile = useMemo(() => {
    let username = '',
      userInfo = '',
      isSubMember = false,
      subMemberId = 0;
    if (profile && profile.result) {
      username = changeName ? changeName : profile.result.username;
      userInfo = profile.result?.vague_email_v2 || profile.result.vague_mobile;
      isSubMember = profile.result.is_submember;
      subMemberId = profile.result.id;
    }
    return { username, userInfo, isSubMember, subMemberId };
  }, [profile, changeName]);

  const modifyNickNameSuccess = (newName: string) => {
    updateUserProfile();
    setChangeName(newName);
  };

  const editClick = () => {
    setVisible(true);
  };

  const getUserName = () => {
    if (copyTraderInfo.nick) {
      return copyTraderInfo.nick;
    }
    return profile?.result?.username
      ? profile?.result.username
      : profile?.result?.vague_email_v2 || profile?.result?.vague_mobile;
  };

  // 是否显示new标签
  const showMasterAvatarNewTag = useCallback(() => {
    const endDate = new Date('2025-01-25 00:00:00').getTime();
    const nowDate = Date.now();
    const avatarNewTag = localStorage.getItem('avatarNewTag');

    return endDate - nowDate >= 0 && avatarNewTag !== '1';
  }, []);

  const MasterAvatar = () => {
    return (
      <div
        className="master-avatar"
        onClick={() => {
          localStorage.setItem('avatarNewTag', '1');
          openSecuritySettingModal(SecuritySettingModalNames.Avatar);
          sensors?.click?.({
            button_name: 'change_avatar_pic',
          });
        }}
      >
        {avatar.avatar_url ? (
          <img
            className="avatar-img"
            src={avatar.avatar_url}
            alt={String(avatar.avatar_id)}
          />
        ) : (
          <img className="avatar-img" src={AvatarIcon} alt="" />
        )}
        {showMasterAvatarNewTag() && (
          <Tag
            variant="primary"
            size="normal"
            color="brand-gradient"
            className="avatar-new-tag"
          >
            {$t('avatarNewTag')}
          </Tag>
        )}
        <span className="master-avatar-edit-btn">
          <EditIcon />
        </span>
      </div>
    );
  };

  const SubaccountAvatar = () => {
    if (copyTraderInfo.photo) {
      return (
        <img
          className="avatar-img"
          src={copyTraderInfo.photo}
          alt={String(copyTraderInfo.photo)}
        />
      );
    }
    const firstWord = getUserName()?.split('')?.[0] || '';
    return (
      <div className="nickName-subaccount-avatar">
        <span>{firstWord?.toLocaleUpperCase?.() || firstWord}</span>
      </div>
    );
  };

  return (
    <>
      <div className="nickName-security-level-container">
        <div className="nickName-uid-security">
          <section style={{ display: 'flex' }}>
            {isSubmember ? <SubaccountAvatar /> : <MasterAvatar />}
            <div className="nickName-text-wrap">
              <div
                className={classNames('nickName-edit', {
                  'level-empty':
                    isSubmember || !(level > 0 || profile?.result?.vip?.is_vip),
                  'submember-nickname': isSubmember,
                })}
              >
                <span className="nickname">{getUserName()}</span>

                {!isCopyTrader && !copyTradingPro && (
                  <span className="edit-icon">
                    <EditIcon
                      onClick={editClick}
                      style={{ width: '16px', height: '16px' }}
                    />
                  </span>
                )}
              </div>
              <div className="nickname-extra">
                {(level > 0 || profile?.result?.loyalty_vip_info?.is_vip) && (
                  <div className="user-security-level">
                    {profile?.result?.loyalty_vip_info?.is_vip &&
                      !isSubmember && (
                        <Suspense fallback="">
                          <VipLevelIcon
                            level={
                              profile?.result?.loyalty_vip_info?.label_level
                            }
                            type={profile?.result?.loyalty_vip_info?.label_type}
                          />
                        </Suspense>
                      )}
                    {level > 0 && !isSubmember && (
                      <div
                        className={classNames(
                          'user-security-level-desc user-security-level-desc-high',
                        )}
                      >
                        <img src={RightIcon} alt="kycVerified" />
                        {level !== 3 && (
                          <>
                            {$t('kycAuth')} Lv.{level}
                          </>
                        )}
                        {level === 3 && (
                          <>{$t('kycCompleteEnterpriseCertification')}</>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
          <div className="user-info-sub-description">
            <div>
              <div className="uid-copy">
                <div className="sub-title">{$t('UID')}</div>
                <Paragraph
                  copyable={{
                    text: profile?.result?.id?.toString(),
                    icon: [
                      <i
                        key="copy-icon"
                        className="moly-iconfont bit-icon_copy text-[16px]"
                      />,
                    ],
                    tooltips: [''],
                  }}
                >
                  <span className="sub-title-desc">{profile?.result?.id}</span>
                </Paragraph>
              </div>
            </div>

            <div className="last-login-time-container">
              <span className="last-login-time">
                <div className="sub-title">{$t('Lastlogintime')}：</div>
                <div className="last-login-record sub-title-desc">
                  <div>
                    <span className="last-login-device">
                      <span className={'device-icon mr-[4px]'}>
                        {TG_PLATFORM[
                          user_agent?.toLowerCase() as keyof typeof TG_PLATFORM
                        ] ? (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 21 21"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M18.3232 2.92495L2.22267 9.15538C2.21309 9.15907 2.20381 9.16348 2.19489 9.16857C2.06433 9.24253 1.15319 9.79984 2.43796 10.2995L2.45115 10.3044L6.28497 11.543C6.31422 11.5525 6.34519 11.5555 6.37572 11.5517C6.40625 11.548 6.4356 11.5377 6.46171 11.5214L15.9707 5.58859C15.994 5.57403 16.02 5.56424 16.0471 5.55977C16.1794 5.53789 16.5603 5.49692 16.3193 5.80283C16.0468 6.15006 9.55175 11.9704 8.83089 12.6159C8.78932 12.6532 8.7636 12.705 8.75901 12.7607L8.44442 16.5108C8.44438 16.549 8.45324 16.5867 8.47029 16.6209C8.48735 16.6551 8.51214 16.6848 8.54268 16.7077C8.58603 16.735 8.637 16.7477 8.68808 16.7438C8.73916 16.7399 8.78765 16.7197 8.82637 16.6862L11.1042 14.6504C11.1402 14.6182 11.1861 14.5993 11.2343 14.5969C11.2825 14.5944 11.3301 14.6085 11.3692 14.6368L15.3433 17.5223L15.3561 17.531C15.4523 17.5904 16.4912 18.1949 16.7912 16.8449L19.5344 3.80102C19.5382 3.7597 19.5754 3.30552 19.2507 3.03711C18.9097 2.75654 18.4271 2.89821 18.3545 2.91349C18.3438 2.91647 18.3333 2.9203 18.3232 2.92495Z"
                              fill="currentColor"
                            />
                          </svg>
                        ) : user_agent?.toLowerCase() === 'pc' ? (
                          <PcIcon className="bit-icon_pc text-[16px]" />
                        ) : (
                          <PhoneIcon className="bit-icon_pad text-[16px] " />
                        )}
                      </span>
                      {TG_PLATFORM[
                        user_agent?.toLowerCase() as keyof typeof TG_PLATFORM
                      ] || user_agent}
                    </span>
                  </div>
                  <span>{dayjs(created_at).format('YYYY-MM-DD HH:mm')}</span>
                  <span>
                    <img
                      onClick={() => {
                        navigate(
                          '/app/user/account-activity?active-type=login',
                        );
                      }}
                      src={TimeLock}
                      alt=""
                      className="last-login-icon"
                    />
                  </span>
                </div>
              </span>
            </div>
          </div>
        </div>

        {!isSubmember && (
          <div className="security-level-wrapper">
            <div className="security-level-container">
              <div className="sub-title">{$t('Security Level')}：</div>
              <span
                className={classNames(
                  'security-level low-text sub-title-desc',
                  {
                    'low-level-color': security_level === 1,
                    'middle-level-color': security_level === 2,
                    'high-level-color': security_level === 3,
                  },
                )}
              >
                {levelText[security_level]}
              </span>
            </div>
            <div
              className={`security-status-icon ${securityIconClass[security_level]}`}
            >
              <div className={securityClass[security_level]}></div>
              <>
                <div className="security-status-icon-text">
                  <div className="top-text">
                    {securityNoticeText[security_level || 1]}
                  </div>
                  <div className="bottom-text">
                    <Space wrap={true} size={12}>
                      {suggest_options.map((item: any) => (
                        <SecurityItem
                          security_level={security_level}
                          key={item.name}
                          item={item}
                        />
                      ))}
                    </Space>
                  </div>
                </div>
                {securityLevelIcon[security_level] && (
                  <div className="security-status-img">
                    <img src={securityLevelIcon[security_level]} alt="" />
                  </div>
                )}
              </>
            </div>
          </div>
        )}
      </div>
      <Suspense fallback={<></>}>
        {visible && (
          <NickNameModal
            name={userProfile.username || userProfile.userInfo}
            visible={visible}
            setVisible={setVisible}
            onSuccess={modifyNickNameSuccess}
            isSubAccount={userProfile.isSubMember}
          />
        )}
      </Suspense>
    </>
  );
};
export default NickNameSecurityLevel;
